import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api, { timeAgo, fmtDate } from "../lib/api";
import { StatusBadge, TypeBadge, PriorityBadge } from "../components/badges";

const CHART_COLORS = ["#00E5FF", "#00E676", "#FFC400"];
const ALL_CAPS = ["STATUS", "TASK", "RESULT", "ALERT", "METRICS", "EVENT"];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [keys, setKeys] = useState([]);
  const [editingUrl, setEditingUrl] = useState(false);
  const [deliverySecret, setDeliverySecret] = useState(null);
  const [secretInput, setSecretInput] = useState("");
  const [urlDraft, setUrlDraft] = useState("");

  useEffect(() => {
    api.get(`/projects/${projectId}`).then(({ data }) => setProject(data)).catch(() => {});
    api.get(`/metrics?source=${projectId}&limit=30`).then(({ data }) => setMetrics(data.reverse())).catch(() => {});
    api.get(`/tasks?destination=${projectId}&limit=10`).then(({ data }) => setTasks(data)).catch(() => {});
    api.get(`/messages?project=${projectId}&limit=15`).then(({ data }) => setMessages(data)).catch(() => {});
    api.get(`/alerts?source=${projectId}&limit=10`).then(({ data }) => setAlerts(data)).catch(() => {});
    api.get(`/projects/${projectId}/keys`).then(({ data }) => setKeys(data)).catch(() => {});
  }, [projectId]);

  const setEnvironment = async (env) => {
    try {
      const { data } = await api.patch(`/projects/${projectId}/environment`, { environment: env });
      setProject(data);
      toast.success(`Environnement → ${env}`);
    } catch {
      toast.error("Modification refusée");
    }
  };

  const toggleCap = async (c) => {
    const has = project.capabilities.includes(c);
    const caps = has ? project.capabilities.filter((x) => x !== c) : [...project.capabilities, c];
    try {
      const { data } = await api.patch(`/projects/${projectId}/capabilities`, { capabilities: caps });
      setProject(data);
      toast.success(`Capacité ${c} ${has ? "retirée" : "déclarée"}`);
    } catch {
      toast.error("Modification refusée");
    }
  };

  const togglePerm = async (k) => {
    const next = !project.permissions[k];
    try {
      const { data } = await api.patch(`/projects/${projectId}/permissions`, { [k]: next });
      setProject(data);
      toast.success(`${k} → ${next ? "OUI" : "NON"}`);
    } catch {
      toast.error("Modification refusée");
    }
  };

  const togglePause = async () => {
    const next = !project.paused;
    if (next && !window.confirm("Suspendre TOUTES les communications sortantes vers ce projet (tâches + pings Health Monitor) ? La réception entrante reste active.")) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/pause`, { paused: next });
      setProject(data);
      toast.success(next ? "Communications suspendues" : "Communications rétablies");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modification refusée");
    }
  };

  const saveApiUrl = async () => {
    try {
      const { data } = await api.patch(`/projects/${projectId}/api_url`, { api_url: urlDraft });
      setProject(data);
      setEditingUrl(false);
      toast.success("Endpoint mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modification refusée");
    }
  };

  if (!project) return <div className="font-mono text-xs text-neutral-500">Chargement…</div>;

  const numericKeys = metrics.length
    ? Object.keys(metrics[metrics.length - 1].metrics || {}).filter((k) => typeof metrics[metrics.length - 1].metrics[k] === "number").slice(0, 3)
    : [];
  const chartData = metrics.map((m) => ({
    time: fmtDate(m.created_at),
    ...Object.fromEntries(numericKeys.map((k) => [k, m.metrics?.[k] ?? null])),
  }));

  return (
    <div data-testid="project-detail-page" className="relative space-y-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <Link to="/projects" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 transition-colors duration-150 hover:text-primary">
        <ArrowLeft size={12} /> Retour aux projets
      </Link>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold uppercase tracking-widest text-white">{project.name}</h1>
          <p className="mt-1 font-mono text-xs text-neutral-500">{project.type} · {project.project_id}</p>
        </div>
        <div className="flex items-center gap-3">
          {project.project_id !== "nexus_self" && (
            <button data-testid="toggle-pause-btn" onClick={togglePause}
              className={`flex items-center gap-1.5 border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-150 ${
                project.paused
                  ? "border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10"
                  : "border-sky-400/40 text-sky-300 hover:bg-sky-400/10"}`}>
              {project.paused ? <><Play size={12} /> Rétablir</> : <><Pause size={12} /> Suspendre</>}
            </button>
          )}
          <StatusBadge status={project.status} />
        </div>
      </div>
      {project.paused && (
        <div data-testid="paused-banner" className="border border-sky-400/40 bg-sky-400/5 px-4 py-2.5 font-mono text-xs text-sky-300">
          ⏸ Communications sortantes suspendues par Mario — aucune tâche ni ping Health Monitor ne sera envoyé à ce projet. La réception entrante reste active.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border border-white/10 bg-card p-5 shadow-[0_0_16px_rgba(0,229,255,0.04)]">
          <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Fiche du projet</div>
          <div className="space-y-2 font-mono text-xs text-neutral-300">
            <div className="flex items-center justify-between gap-2">
              <span className="text-neutral-600">Endpoint</span>
              {editingUrl ? (
                <span className="flex flex-1 items-center gap-1">
                  <input data-testid="api-url-input" value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveApiUrl(); if (e.key === "Escape") setEditingUrl(false); }}
                    className="w-full border border-primary/40 bg-[#050505] px-2 py-1 font-mono text-xs text-white outline-none" autoFocus />
                  <button data-testid="api-url-save" onClick={saveApiUrl} className="border border-[#00E676]/40 px-2 py-1 text-[10px] uppercase text-[#00E676] hover:bg-[#00E676]/10">OK</button>
                  <button data-testid="api-url-cancel" onClick={() => setEditingUrl(false)} className="border border-border px-2 py-1 text-[10px] uppercase text-neutral-500 hover:text-white">✕</button>
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="max-w-[180px] truncate" title={project.api_url}>{project.api_url}</span>
                  {project.project_id !== "nexus_self" && (
                    <button data-testid="api-url-edit" onClick={() => { setUrlDraft(project.api_url); setEditingUrl(true); }}
                      className="border border-border px-1.5 py-0.5 text-[10px] uppercase text-neutral-500 transition-colors duration-150 hover:border-primary/50 hover:text-primary">
                      modifier
                    </button>
                  )}
                </span>
              )}
            </div>
            <div className="flex justify-between"><span className="text-neutral-600">Auth</span><span>{project.auth_type}</span></div>
            <div className="flex items-center justify-between"><span className="text-neutral-600">Environnement</span>
              <select data-testid="environment-select" value={project.environment || "test"} onChange={(e) => setEnvironment(e.target.value)}
                className={`border bg-[#050505] px-2 py-0.5 font-mono text-[10px] uppercase outline-none ${project.environment === "production" ? "border-[#00E676]/40 text-[#00E676]" : project.environment === "simulator" ? "border-amber-500/40 text-amber-300" : "border-border text-neutral-400"}`}>
                {["production", "preview", "test", "simulator", "internal"].map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div className="flex justify-between"><span className="text-neutral-600">Dernière comm.</span><span>{timeAgo(project.last_seen_at)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">Créé le</span><span>{fmtDate(project.created_at)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">Latence</span><span>{project.health?.latency_ms != null ? `${project.health.latency_ms} ms` : "—"}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">Échecs consécutifs</span><span className={project.health?.consecutive_failures ? "text-red-400" : "text-[#00E676]"}>{project.health?.consecutive_failures ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">Santé rapportée</span><span>{project.health?.reported_health || "—"}</span></div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-600">Secret de livraison</span>
              <span className="flex items-center gap-2">
                <span className={project.delivery_secret ? "text-[#00E676]" : "text-neutral-600"}>{project.delivery_secret ? "configuré" : "aucun"}</span>
                {project.project_id !== "nexus_self" && (
                  <button data-testid="delivery-secret-btn"
                    onClick={async () => {
                      if (!window.confirm("Générer un secret de livraison ? NEXUS l'enverra en Authorization: Bearer sur CHAQUE livraison vers ce projet. Le récepteur du projet devra le vérifier. Le secret ne sera affiché qu'une seule fois.")) return;
                      try {
                        const { data } = await api.post(`/projects/${projectId}/delivery-secret/regenerate`);
                        setDeliverySecret(data.delivery_secret);
                        setProject({ ...project, delivery_secret: true });
                        toast.success("Secret généré — copiez-le maintenant, il ne sera plus affiché");
                      } catch (e) { toast.error(e?.response?.data?.detail || "Génération refusée"); }
                    }}
                    className="border border-border px-1.5 py-0.5 text-[10px] uppercase text-neutral-500 transition-colors duration-150 hover:border-primary/50 hover:text-primary">
                    {project.delivery_secret ? "régénérer" : "générer"}
                  </button>
                )}
              </span>
            </div>
            {deliverySecret && (
              <div data-testid="delivery-secret-reveal" className="mt-2 border border-[#00E676]/40 bg-[#00E676]/5 p-2">
                <div className="mb-1 text-[10px] uppercase tracking-[0.15em] text-[#00E676]">Secret — affiché une seule fois</div>
                <div className="break-all text-[11px] text-white">{deliverySecret}</div>
                <button data-testid="delivery-secret-copy" onClick={() => { navigator.clipboard.writeText(deliverySecret).catch(() => {}); toast.success("Secret copié"); }}
                  className="mt-1.5 border border-[#00E676]/40 px-2 py-0.5 text-[10px] uppercase text-[#00E676] hover:bg-[#00E676]/10">Copier</button>
              </div>
            )}
            {project.project_id !== "nexus_self" && (
              <div className="mt-2 border border-border/60 p-2">
                <div className="mb-1 text-[10px] uppercase tracking-[0.15em] text-neutral-500">Définir un secret existant (déjà installé côté projet)</div>
                <div className="flex gap-1.5">
                  <input data-testid="delivery-secret-set-input" type="password" value={secretInput} onChange={(e) => setSecretInput(e.target.value)}
                    placeholder="Coller le secret exact…"
                    className="min-w-0 flex-1 border border-border bg-transparent px-2 py-1 text-[11px] text-white outline-none focus:border-primary/50" />
                  <button data-testid="delivery-secret-set-btn"
                    onClick={async () => {
                      const v = secretInput.trim();
                      if (v.length < 20) { toast.error("Secret trop court (min. 20 caractères)"); return; }
                      try {
                        const { data } = await api.post(`/projects/${projectId}/delivery-secret`, { secret: v });
                        setSecretInput("");
                        setProject({ ...project, delivery_secret: true });
                        toast.success(`Secret défini — empreinte ${data.prefix}…/${data.length}/${data.sha256_8}`, { duration: 12000 });
                      } catch (e) { toast.error(e?.response?.data?.detail || "Définition refusée"); }
                    }}
                    className="border border-primary/50 px-2 py-1 text-[10px] uppercase text-primary transition-colors duration-150 hover:bg-primary/10">
                    Définir
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {ALL_CAPS.map((c) => {
              const active = project.capabilities.includes(c);
              return (
                <button key={c} data-testid={`toggle-cap-${c.toLowerCase()}`} onClick={() => toggleCap(c)} title="Cliquer pour déclarer/retirer"
                  className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-150 ${active ? "border-primary/50 bg-primary/10 text-primary hover:border-primary" : "border-border text-neutral-600 hover:border-neutral-500 hover:text-neutral-400"}`}>
                  {c}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-1 font-mono text-[10px] text-neutral-500">
            {Object.entries(project.permissions || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span>{k}</span>
                <button data-testid={`toggle-perm-${k}`} onClick={() => togglePerm(k)} title="Cliquer pour modifier"
                  className={`border px-2 py-0.5 font-mono text-[10px] uppercase transition-colors duration-150 ${v ? "border-[#00E676]/40 text-[#00E676] hover:border-[#00E676]" : "border-red-500/40 text-red-400 hover:border-red-500"}`}>
                  {v ? "OUI" : "NON"}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-white/10 bg-card p-5 shadow-[0_0_16px_rgba(0,229,255,0.04)] lg:col-span-2">
          <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Métriques</div>
          {chartData.length > 0 && numericKeys.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#1A1A1A" />
                <XAxis dataKey="time" stroke="#555" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                <YAxis stroke="#555" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #1A1A1A", fontFamily: "JetBrains Mono", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                {numericKeys.map((k, i) => (
                  <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[220px] items-center justify-center font-mono text-xs text-neutral-600">
              Aucune métrique reçue pour l'instant
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="border border-white/10 bg-card shadow-[0_0_16px_rgba(0,229,255,0.04)]">
          <div className="border-b border-border px-5 py-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">Tâches récentes</div>
          <div className="divide-y divide-border">
            {tasks.length === 0 && <div className="px-5 py-6 font-mono text-xs text-neutral-600">Aucune tâche</div>}
            {tasks.map((t) => (
              <div key={t.task_id} className="flex items-center justify-between px-5 py-3 font-mono text-xs">
                <div>
                  <span className="text-white">{t.action}</span>
                  <span className="ml-2 text-neutral-600">{t.task_id}</span>
                </div>
                <span className={t.status === "completed" ? "text-[#00E676]" : "text-amber-300"}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-card shadow-[0_0_16px_rgba(0,229,255,0.04)]">
          <div className="border-b border-border px-5 py-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">Alertes</div>
          <div className="divide-y divide-border">
            {alerts.length === 0 && <div className="px-5 py-6 font-mono text-xs text-neutral-600">Aucune alerte</div>}
            {alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-5 py-3">
                <span className="font-mono text-xs text-neutral-300">[{a.category}] {a.message}</span>
                <PriorityBadge priority={a.priority} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-white/10 bg-card shadow-[0_0_16px_rgba(0,229,255,0.04)]">
        <div className="border-b border-border px-5 py-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">Clés API — cycle de vie</div>
        <div data-testid="api-keys-list" className="divide-y divide-border">
          {keys.length === 0 && <div className="px-5 py-6 font-mono text-xs text-neutral-600">Aucune clé</div>}
          {keys.map((k) => (
            <div key={k.key_id} className="flex items-center justify-between px-5 py-3 font-mono text-[11px]">
              <div className="flex items-center gap-4">
                <span className="text-neutral-500">sha256:{k.hash_prefix}…</span>
                <span className={k.status === "active" ? "text-[#00E676] uppercase" : "text-red-400 uppercase"}>{k.status === "active" ? "ACTIVE" : "RÉVOQUÉE"}</span>
              </div>
              <div className="flex gap-5 text-neutral-500">
                <span>créée {fmtDate(k.created_at)}</span>
                <span>dernier usage {k.last_used_at ? fmtDate(k.last_used_at) : "—"}</span>
                {k.revoked_at && <span className="text-red-400/70">révoquée {fmtDate(k.revoked_at)}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-white/10 bg-card shadow-[0_0_16px_rgba(0,229,255,0.04)]">
        <div className="border-b border-border px-5 py-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">Communications récentes</div>
        <div className="divide-y divide-border">
          {messages.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-5 py-2.5">
              <div className="flex items-center gap-3">
                <TypeBadge type={m.type} />
                <span className="font-mono text-[11px] text-neutral-300">{m.source} → {m.destination}</span>
              </div>
              <span className="font-mono text-[10px] text-neutral-600">{fmtDate(m.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
